import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
  SubmittedPostVersionEnum,
} from '@/app/common/general-types/briefs.dto';
import { CreateBriefApplicationDto } from '@/app/common/general-types/brief-application.dto';
import { PricingModelV2Dto } from '@/app/common/general-types/pricing.dto';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { ImageService } from '@/app/services/image/image.service';
import { ApplicationStatusBadgeComponent } from '@/app/atoms/application-status-badge/application-status-badge.component';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { CreatorApplicationActions } from './molecules/application-actions/application-actions';
import {
  channelsOfferedAtApply,
  placementLabelForChannelId,
  socialChannelIdsOnPieces,
} from '@/app/common/brief-pieces/piece-rules';

@Component({
  selector: 'ins-creator-application-drawer',
  imports: [DatePipe, CurrencyPipe, ApplicationStatusBadgeComponent, LdgImgComponent, CreatorApplicationActions],
  templateUrl: './application-drawer.html',
  styleUrl: './application-drawer.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorApplicationDrawer {
  @ViewChild('applicationDrawerDialog', { static: false })
  applicationDrawerDialog?: ElementRef<HTMLDialogElement>;

  close = output<void>();
  applicationUpdated = output<BriefApplicationResponseDto>();

  readonly application = signal<BriefApplicationResponseDto | undefined>(undefined);
  readonly actionLoading = signal(false);
  /** Social channels the creator is offering on a piece-list brief. UGC is not included. */
  readonly offeredChannelIds = signal<number[]>([]);

  readonly imageService = inject(ImageService);
  private briefApplicationService = inject(BriefApplicationService);
  private firebaseService = inject(FirebaseService);
  private toastNotificationService = inject(ToastNotificationService);

  readonly ApplicationStatusEnum = ApplicationStatusEnum;

  open(application: BriefApplicationResponseDto): void {
    this.application.set(application);
    this.offeredChannelIds.set([]);
    this.applicationDrawerDialog?.nativeElement.showModal();
  }

  closeDrawer(): void {
    this.applicationDrawerDialog?.nativeElement.close();
    this.application.set(undefined);
    this.close.emit();
  }

  apply(): void {
    const app = this.application();
    if (!app) {
      return;
    }

    this.actionLoading.set(true);

    const pieces = app.brief?.pieces;
    const offer = pieces?.length
      ? channelsOfferedAtApply(this.offeredChannelIds(), pieces)
      : undefined;
    const payload: CreateBriefApplicationDto = {
      briefId: app.briefId,
      creatorUserId: app.creatorUserId,
      applicationStatus: ApplicationStatusEnum.Applied,
      hiredPricing: app.hiredBriefPricing as PricingModelV2Dto | undefined,
      createdBy: this.firebaseService.userId!,
      resultsVersion: SubmittedPostVersionEnum.SuperSocial,
      ...(offer ? { crossPlatformsPutForward: offer } : {}),
    };

    this.briefApplicationService.applyforCreator(payload).subscribe({
      next: (updated) => {
        this.actionLoading.set(false);
        const merged = { ...app, ...updated };
        this.application.set(merged);
        this.applicationUpdated.emit(merged);
        void this.toastNotificationService.alert({
          title: 'Applied',
          message: 'Application status updated to Applied.',
          type: 'success',
          icon: 'success',
        });
      },
      error: () => {
        this.actionLoading.set(false);
        void this.toastNotificationService.alert({
          title: 'Apply failed',
          message: 'Could not apply on behalf of creator.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  briefTitle(): string {
    return this.application()?.brief?.title ?? 'Untitled brief';
  }

  campaignName(): string {
    return this.application()?.brief?.campaign?.campaignName ?? '—';
  }

  brandName(): string {
    return this.application()?.brief?.businessAccount?.companyName ?? '—';
  }

  offerablePieceChannels(): { id: number; label: string; offered: boolean }[] {
    const pieces = this.application()?.brief?.pieces;
    if (!pieces?.length || this.application()?.applicationStatus !== ApplicationStatusEnum.Invited) {
      return [];
    }
    const offered = new Set(this.offeredChannelIds());
    return socialChannelIdsOnPieces(pieces).map((id) => ({
      id,
      label: placementLabelForChannelId(id),
      offered: offered.has(id),
    }));
  }

  toggleOfferedChannel(channelId: number): void {
    this.offeredChannelIds.update((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId],
    );
  }

  hiredCurrencyCode(): string {
    const pricing = this.application()?.hiredBriefPricing as { currencyCode?: string } | undefined;
    return (pricing?.currencyCode ?? 'gbp').toUpperCase();
  }
}
